import { useCallback, useEffect, useState } from "react";
import { Bot, RotateCcw } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { usePageBack } from "../context/PageBackContext.js";
import { useToast } from "../context/ToastContext.js";
import { useLookup } from "../context/LookupContext.js";
import { refreshVirtualHr } from "../lib/virtualHr.js";
import { useConfirm } from "../components/ui/confirm-dialog.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Checkbox } from "../components/ui/checkbox.js";
import { Input } from "../components/ui/input.js";
import { SelectField } from "../components/ui/select-field.js";
import { Textarea } from "../components/ui/textarea.js";

interface VhrDocument {
  docType: string;
  builtIn: boolean;
  label: string;
  enabled: boolean;
  verifyPrompt: string;
  verifyIsCustom: boolean;
  /** The default installed in the database; null for a document the admin added, or when defaults are not installed. */
  defaultVerifyPrompt: string | null;
  extractable: boolean;
  extractPrompt: string | null;
  extractIsCustom: boolean;
  defaultExtractPrompt: string | null;
  updatedAt: string | null;
}

/** Text shared by every document check, for both places. The reply format is shown but locked. */
interface VhrShared {
  rules: string;
  rulesIsCustom: boolean;
  defaultRules: string;
  identityRules: string;
  identityIsCustom: boolean;
  defaultIdentityRules: string;
  lockedSchema: string;
}

interface VhrData {
  config: { enabled: boolean; assistantName: string; employeeEnabled: boolean; onboardingEnabled: boolean };
  documents: VhrDocument[];
  shared: VhrShared;
  /** False until the migration that installs the default prompts has run on this database. */
  defaultsInstalled: boolean;
}

type ConfigPatch = {
  enabled?: boolean;
  assistantName?: string;
  employeeEnabled?: boolean;
  onboardingEnabled?: boolean;
  /** null = back to the default text. */
  sharedRules?: string | null;
  identityRules?: string | null;
};

/** The identity instructions must keep these, or the name check stops working (the server enforces it too). */
const identityIsValid = (text: string): boolean => text.includes('"identityMatch"') && text.includes("{{referenceName}}");

/** One place Sirra can be switched on or off (Employee section, Onboarding). Only counts while the master switch is on. */
function ContextSwitch({ title, description, offNote, checked, masterOn, onChange }: {
  title: string;
  description: string;
  offNote: string;
  checked: boolean;
  masterOn: boolean;
  onChange: (on: boolean) => void;
}) {
  const active = masterOn && checked;
  return (
    <div className="space-y-2 rounded-lg border border-surface-200 p-4">
      <div className="flex items-center justify-between gap-3">
        <label className={masterOn ? "flex cursor-pointer items-center gap-2.5" : "flex items-center gap-2.5 opacity-60"}>
          <Checkbox checked={checked} disabled={!masterOn} onCheckedChange={(v) => onChange(v === true)} aria-label={`${title} on`} />
          <span className="text-[14px] font-semibold">{title}</span>
        </label>
        <Badge tone={active ? "success" : "warning"}>{active ? "Sirra on" : "Plain upload"}</Badge>
      </div>
      <p className="text-[12.5px] text-surface-500">{description}</p>
      {masterOn && !checked && (
        <p className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[12.5px] text-[var(--color-warning-fg)]">{offNote}</p>
      )}
      {!masterOn && <p className="text-[12px] text-surface-400">Turn Virtual HR on above to choose this.</p>}
    </div>
  );
}

function decodeRole(token: string): string {
  try {
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["role"] ?? "");
  } catch { return ""; }
}

/**
 * Virtual HR (Sirra) settings, Super HR Admin only (the API enforces it too):
 *  - the assistant's name and a master switch;
 *  - a separate switch for the Employee section and for Onboarding. Unticked, documents there are
 *    uploaded as they are: no check, no details read, no autofill and, in onboarding, no Sirra;
 *  - the instructions every check uses: shared rules, identity rules, and one section per document.
 * HR's employee profile and onboarding use the same prompts: there is one set, edited here.
 */
export default function VirtualHrAdminPage() {
  usePageBack("/admin", "Back to Admin");
  const { accessToken } = useAuth();
  const allowed = (accessToken ? decodeRole(accessToken) : "") === "SUPER_HR_ADMIN";
  const toast = useToast();
  const confirm = useConfirm();
  const docTypeOptions = useLookup("DOCUMENT_TYPE");

  const [data, setData] = useState<VhrData | null>(null);
  const [name, setName] = useState("");
  const [drafts, setDrafts] = useState<Record<string, { verify: string; extract: string }>>({});
  const [sharedDraft, setSharedDraft] = useState({ rules: "", identity: "" });
  const [newType, setNewType] = useState("");

  const apply = useCallback((d: VhrData) => {
    setData(d);
    setName(d.config.assistantName);
    setDrafts(Object.fromEntries(d.documents.map((x) => [x.docType, { verify: x.verifyPrompt, extract: x.extractPrompt ?? "" }])));
    setSharedDraft({ rules: d.shared.rules, identity: d.shared.identityRules });
  }, []);

  useEffect(() => {
    if (!allowed) return;
    api.get<VhrData>("/admin/virtual-hr").then(({ data: d }) => apply(d)).catch((err) => {
      logError("VirtualHrAdminPage.load", err);
      toast.show(errMsg(err) ?? "Could not load Virtual HR settings", "error");
    });
  }, [allowed, apply, toast]);

  if (!allowed) return <div className="p-8 text-sm text-surface-600">Access denied. Super HR Admin only.</div>;
  if (!data) return <div className="p-8 text-sm text-surface-500">Loading…</div>;

  async function call<T>(fn: () => Promise<T>, ok: string): Promise<T> {
    try {
      const res = await fn();
      toast.show(ok, "success");
      void refreshVirtualHr();
      return res;
    } catch (err) {
      toast.show(errMsg(err) ?? "Could not save", "error");
      throw err;
    }
  }

  const saveConfig = (patch: ConfigPatch) =>
    call(() => api.patch("/admin/virtual-hr/config", patch), "Saved.").then(() => api.get<VhrData>("/admin/virtual-hr")).then(({ data: d }) => apply(d));

  const saveDoc = (docType: string, body: Record<string, unknown>, ok = "Saved.") =>
    call(() => api.put<VhrData>(`/admin/virtual-hr/documents/${docType}`, body), ok).then(({ data: d }) => apply(d));

  async function resetDoc(doc: VhrDocument) {
    const yes = await confirm({
      title: `Reset ${doc.label}?`,
      description: doc.builtIn ? "Your prompts for this document are removed and the default ones are used again." : "This document is removed from Virtual HR.",
      actionText: "Reset",
      tone: "destructive",
    });
    if (!yes) return;
    await call(() => api.delete<VhrData>(`/admin/virtual-hr/documents/${doc.docType}`), "Reset.").then(({ data: d }) => apply(d));
  }

  async function resetShared() {
    const yes = await confirm({
      title: "Reset the shared instructions?",
      description: "Your general rules and identity instructions are removed and the default ones are used again.",
      actionText: "Reset",
      tone: "destructive",
    });
    if (!yes) return;
    await saveConfig({ sharedRules: null, identityRules: null }).catch(() => {});
  }

  const sharedDirty = !!data && (sharedDraft.rules !== data.shared.rules || sharedDraft.identity !== data.shared.identityRules);
  const sharedCustom = data.shared.rulesIsCustom || data.shared.identityIsCustom;
  const identityOk = identityIsValid(sharedDraft.identity);
  const configured = new Set(data.documents.map((d) => d.docType));
  const addable = docTypeOptions.filter((o) => /^[A-Z0-9_]{2,40}$/.test(o.value) && !configured.has(o.value));

  return (
    <div className="mx-auto w-full max-w-[1000px] space-y-5 p-4">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-xl bg-[var(--color-accent-bg)] text-[var(--color-accent-fg)]"><Bot size={20} /></span>
        <div>
          <h1 className="text-[22px] font-bold">Virtual HR</h1>
          <p className="text-[13px] text-surface-500">The assistant that checks uploaded documents. Choose where it works, edit its name, and edit the instructions it follows. HR's employee profile and onboarding use the same instructions.</p>
        </div>
      </div>

      {!data.defaultsInstalled && (
        <p className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[12.5px] text-[var(--color-warning-fg)]">
          The default prompts are not installed in this database yet, so documents cannot be checked: they are accepted and left for HR to review by hand. Run the database migrations (<code>pnpm --filter @hrms/api run db:migrate</code>) to install them. A prompt you write yourself works without them.
        </p>
      )}

      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2.5">
            <Checkbox checked={data.config.enabled} onCheckedChange={(v) => void saveConfig({ enabled: v === true }).catch(() => {})} aria-label="Virtual HR on" />
            <span className="text-[14px] font-semibold">Virtual HR is {data.config.enabled ? "ON" : "OFF"}</span>
          </label>
          <Badge tone={data.config.enabled ? "success" : "warning"}>{data.config.enabled ? "Checking documents" : "Skipped"}</Badge>
        </div>
        {!data.config.enabled && (
          <p className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[12.5px] text-[var(--color-warning-fg)]">
            Off everywhere: documents are uploaded directly in both places. Nothing is verified, no details are read, nothing is filled into the profile, and Sirra does not appear. HR can still verify a document by hand.
          </p>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <ContextSwitch
            title="Employee section"
            description="HR's employee profile: documents HR, or the employee, uploads there."
            offNote="Documents in the employee profile are uploaded as they are: no check, no details read, nothing auto-filled."
            checked={data.config.employeeEnabled}
            masterOn={data.config.enabled}
            onChange={(v) => void saveConfig({ employeeEnabled: v }).catch(() => {})}
          />
          <ContextSwitch
            title="Onboarding"
            description="The new-hire wizard, where Sirra also appears and speaks."
            offNote="Documents in onboarding are uploaded as they are: no check, no details read, nothing auto-filled, and Sirra does not appear or speak."
            checked={data.config.onboardingEnabled}
            masterOn={data.config.enabled}
            onChange={(v) => void saveConfig({ onboardingEnabled: v }).catch(() => {})}
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <label className="mb-1 block text-[12px] font-semibold text-surface-600" htmlFor="vhr-name">Assistant name (shown to employees and HR)</label>
            <Input id="vhr-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </div>
          <AsyncButton size="sm" disabled={name.trim().length < 2 || name.trim() === data.config.assistantName} onClick={() => saveConfig({ assistantName: name.trim() })}>
            Save name
          </AsyncButton>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-bold">Shared instructions</h2>
            <p className="text-[12.5px] text-surface-500">Added to every document check, in the Employee section and in Onboarding alike. One set for both.</p>
          </div>
          <Badge tone={sharedCustom ? "accent" : "neutral"}>{sharedCustom ? "Custom" : "Default"}</Badge>
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-surface-600" htmlFor="vhr-rules">General rules: added to every document check, after the document's own prompt</label>
          <Textarea id="vhr-rules" resize="vertical" minRows={4} value={sharedDraft.rules} onChange={(e) => setSharedDraft((d) => ({ ...d, rules: e.target.value }))} className="font-mono text-[12.5px]" />
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-surface-600" htmlFor="vhr-identity">Identity check: used when the employee's Aadhaar or PAN is already on file</label>
          <Textarea id="vhr-identity" resize="vertical" minRows={10} value={sharedDraft.identity} onChange={(e) => setSharedDraft((d) => ({ ...d, identity: e.target.value }))} className="font-mono text-[12.5px]" />
          <p className="mt-1 text-[11.5px] text-surface-500">
            Placeholders: <code>{"{{referenceDoc}}"}</code>, <code>{"{{referenceName}}"}</code>, <code>{"{{referenceDob}}"}</code>. Text between <code>{"{{#strict}}"}</code> and <code>{"{{/strict}}"}</code> is used for PAN, Aadhaar, marksheet and degree; between <code>{"{{#resume}}"}</code> and <code>{"{{/resume}}"}</code> only for a resume.
          </p>
          {!identityOk && (
            <p className="mt-1 text-[12px] font-medium text-[var(--color-error-fg)]">Keep the <code>"identityMatch"</code> key and the <code>{"{{referenceName}}"}</code> placeholder, or the name check stops working.</p>
          )}
        </div>

        <details className="rounded-md border border-surface-200 px-3 py-2">
          <summary className="cursor-pointer text-[12.5px] font-semibold text-surface-600">Reply format (locked: the system reads these exact fields)</summary>
          <Textarea readOnly resize="vertical" minRows={10} value={data.shared.lockedSchema} className="mt-2 font-mono text-[12.5px]" aria-label="Reply format (locked)" />
        </details>

        <div className="flex flex-wrap items-center gap-2">
          <AsyncButton
            size="sm" disabled={!sharedDirty || !sharedDraft.rules.trim() || !sharedDraft.identity.trim() || !identityOk}
            onClick={() => saveConfig({
              // Saving the unchanged default text keeps it on its default.
              sharedRules: sharedDraft.rules === data.shared.defaultRules ? null : sharedDraft.rules,
              identityRules: sharedDraft.identity === data.shared.defaultIdentityRules ? null : sharedDraft.identity,
            })}
          >
            Save shared instructions
          </AsyncButton>
          <Button size="sm" variant="secondary" disabled={!sharedCustom} onClick={() => void resetShared()}>
            <RotateCcw size={13} /> Reset to default
          </Button>
        </div>
      </Card>

      {data.documents.map((doc) => {
        const draft = drafts[doc.docType] ?? { verify: doc.verifyPrompt, extract: doc.extractPrompt ?? "" };
        const set = (patch: Partial<typeof draft>) => setDrafts((d) => ({ ...d, [doc.docType]: { ...draft, ...patch } }));
        const dirty = draft.verify !== doc.verifyPrompt || (doc.extractable && draft.extract !== (doc.extractPrompt ?? ""));
        const custom = doc.verifyIsCustom || doc.extractIsCustom;
        return (
          <Card key={doc.docType} className="space-y-4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-[15px] font-bold">{doc.label}</h2>
                <Badge tone="outline">{doc.docType}</Badge>
                <Badge tone={custom ? "accent" : "neutral"}>{custom ? "Custom prompt" : doc.builtIn ? "Default prompt" : "New"}</Badge>
                <span className="text-[12px] text-surface-500">Used in the Employee section and in Onboarding</span>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium">
                <Checkbox checked={doc.enabled} onCheckedChange={(v) => void saveDoc(doc.docType, { enabled: v === true }, v === true ? "Enabled." : "Skipped for this document.").catch(() => {})} aria-label={`${doc.label} enabled`} />
                Check this document
              </label>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-semibold text-surface-600" htmlFor={`vp-${doc.docType}`}>Verification prompt: decides whether the file is the right, genuine document</label>
              <Textarea id={`vp-${doc.docType}`} resize="vertical" minRows={8} value={draft.verify} onChange={(e) => set({ verify: e.target.value })} className="font-mono text-[12.5px]" />
              {!draft.verify.trim() && (
                <p className="mt-1 text-[12px] font-medium text-[var(--color-warning-fg)]">No prompt yet: this document is not checked until you write one and save it.</p>
              )}
            </div>

            {doc.extractable && (
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-surface-600" htmlFor={`ep-${doc.docType}`}>Extraction prompt: decides which details are read from the file</label>
                <Textarea id={`ep-${doc.docType}`} resize="vertical" minRows={8} value={draft.extract} onChange={(e) => set({ extract: e.target.value })} className="font-mono text-[12.5px]" />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <AsyncButton
                size="sm" disabled={!dirty || !draft.verify.trim()}
                onClick={() => saveDoc(doc.docType, {
                  // Saving the unchanged default text keeps the document on its default.
                  verifyPrompt: draft.verify === doc.defaultVerifyPrompt ? null : draft.verify,
                  ...(doc.extractable ? { extractPrompt: draft.extract === (doc.defaultExtractPrompt ?? "") ? null : draft.extract } : {}),
                })}
              >
                Save prompts
              </AsyncButton>
              <Button size="sm" variant="secondary" disabled={!custom && doc.builtIn} onClick={() => void resetDoc(doc)}>
                <RotateCcw size={13} /> {doc.builtIn ? "Reset to default" : "Remove"}
              </Button>
              {doc.updatedAt && <span className="text-[11.5px] text-surface-400">Last changed {new Date(doc.updatedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
          </Card>
        );
      })}

      <Card className="space-y-3 p-5">
        <h2 className="text-[15px] font-bold">Add a document</h2>
        <p className="text-[13px] text-surface-500">Give another document type its own prompt, for example a passport or a visa. It uses the same checks, with your instructions. It is not checked until you write its prompt.</p>
        <div className="flex flex-wrap items-center gap-3">
          <SelectField className="w-72" value={newType} onChange={(e) => setNewType(e.target.value)}>
            <option value="">Choose a document type…</option>
            {addable.map((o) => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </SelectField>
          <AsyncButton
            size="sm" disabled={!newType}
            onClick={async () => {
              const opt = addable.find((o) => o.value === newType);
              await saveDoc(newType, { label: opt?.displayName ?? newType, enabled: true }, "Document added.");
              setNewType("");
            }}
          >
            Add document
          </AsyncButton>
        </div>
      </Card>
    </div>
  );
}
